import { useCallback, useState } from "react"
import { TextStyle, View, ViewStyle } from "react-native"

import { router } from "expo-router"

import { Button, Screen, Text } from "src/components/Ignite"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useTermsAcceptance } from "src/hooks/useTermsAcceptance"
import { ThemedStyle } from "src/theme"

export default function AcceptTermsScreen() {
  const { themed } = useAppTheme()
  const { logout, clearAllTemporaryData, getUserEmail } = useAuth()
  const { acceptTerms, declineTerms } = useTermsAcceptance(getUserEmail())
  const [isDeclining, setIsDeclining] = useState(false)

  const onAcceptTerms = useCallback(() => {
    acceptTerms()
    router.replace("/setup/accept-ai-processing")
  }, [acceptTerms])

  const onDeclineTerms = useCallback(async () => {
    if (isDeclining) return
    setIsDeclining(true)
    try {
      declineTerms()
      await logout()
      clearAllTemporaryData()
      router.replace("/unauth/login")
    } finally {
      setIsDeclining(false)
    }
  }, [declineTerms, logout, clearAllTemporaryData, isDeclining])

  return (
    <Screen
      preset="scroll"
      style={themed($screenContainer)}
      contentContainerStyle={themed($contentContainer)}
      safeAreaEdges={["top", "bottom"]}
    >
      <View style={themed($content)}>
        <Text preset="heading" text="SWI End User Terms" style={themed($title)} />
        <Text style={[themed($text), { textAlign: "center" }]}>Last Updated: June 5, 2026</Text>

        <Text style={themed($text)}>
          Welcome to the SWI Platform (as defined below). Please read these End User Terms (these
          {" \u201cUser Terms\u201d"} carefully because they govern your use of our multi-agent
          generative AI framework software platform (including any associated desktop and mobile
          device applications ({"\u201cApps\u201d"}) and websites) and related services
          (collectively with any improvements, modifications, features and derivative works thereof,
          the {"\u201cSWI Platform\u201d"}). To make these User Terms easier to read, the terms
          {" \u201cSWI,\u201d \u201cwe,\u201d \u201cour\u201d"} and {"\u201cus\u201d"} include
          Sidekick Wellness Inc. and our affiliates and subsidiaries.{"\n"}
          By using the SWI Platform, you agree to be bound by these User Terms. If you do not agree
          to be bound by these User Terms, do not use the SWI Platform.
        </Text>

        <Text style={themed($text)}>
          1. SWI Platform. The SWI Platform provides organizations with an AI-powered platform for
          delivering resources to their community members, including you.
        </Text>

        <Text style={themed($text)}>
          2. User Terms. You have been invited to use and access the SWI Platform by an organization
          or other third party, which has entered into a separate agreement with SWI to make the SWI
          Platform available to end users for accessing information, resources, experts offered by
          such third party (the {"\u201cOrganization\u201d"}). You acknowledge that your rights to
          use the SWI Platform are subject to the Organization{"\u2019"}s rights and obligations
          under the Organization{"\u2019"}s agreement with SWI to control and manage certain aspects
          of the SWI Platform. For example, Organization may suspend or terminate your account
          and/or access to the SWI Platform, or grant, restrict or modify your ability to access
          certain data or content within the SWI Platform. You further acknowledge that your access
          and use of the SWI Platform will automatically terminate upon the expiration or
          termination of our separate written agreement with the Organization.
        </Text>

        <Text style={themed($text)}>
          3. Changes to Terms or Platform. We may update these User Terms at any time, in our sole
          discretion. If we do so, we{"\u2019"}ll let you know either by posting the updated User
          Terms either on our website, within the SWI Platform or through other communications. It
          {"\u2019"}s important that you review these User Terms whenever we update them or you use
          the SWI Platform. If you continue to use the SWI Platform after we have posted updated
          User Terms, you are agreeing to be bound by the updated User Terms. If you don{"\u2019"}t
          agree to be bound by the updated User Terms, then you may not use the SWI Platform
          anymore. Because the SWI Platform is evolving over time, we may change or discontinue all
          or any aspect of the SWI Platform, at any time and without notice, at our sole discretion.
        </Text>

        <Text style={themed($text)}>
          4. Who May Use the SWI Platform?{"\n\n"}
          (a) Eligibility. You may use the SWI Platform only if you are (i) capable of forming a
          binding contract with SWI; (ii) not barred from using the SWI Platform under applicable
          law; and (iii) authorized to do so by Organization.{"\n\n"}
          (b) Registration and Your Information. In order to use the SWI Platform, you are required
          to create an account or similar profile to use the SWI Platform ({"\u201cAccount\u201d"}).
          You can do this via the SWI Platform through our online registration procedures which we
          may update from time to time. You agree that you won{"\u2019"}t misrepresent your
          identity, use any other person{"\u2019"}s image, likeness or identity, or otherwise
          provide any deceptive or misleading profile information or images in connection with the
          creation and use of your Account.{"\n\n"}
          (c) Privacy and Data Practices. Our collection, use, disclosure, retention, and protection
          of personal information are described in our Privacy Notice. The Privacy Notice explains
          the types of information we collect, how we use and disclose information, how long we
          retain information, and the choices and rights that may be available to you. If you use
          the SWI Platform through an organization, that organization may also provide privacy
          notices, policies, consent forms, or terms that apply to its content, resources, users, or
          programs. SWI is not responsible for the organization{"\u2019"}s privacy practices, but
          SWI will process information in accordance with its agreements with that organization, our
          Privacy Notice, these User Terms, and applicable law.{"\n\n"}
          (d) Accuracy of Account Information. It{"\u2019"}s important that you provide us with
          accurate, complete and up-to-date information for your Account, and you agree to update
          such information to keep it accurate, complete and up to date. If you don{"\u2019"}t, we
          might have to suspend or terminate your Account. You agree that you won{"\u2019"}t
          disclose your Account password to anyone and will use all reasonable means to secure your
          Account password and associated login credentials. You agree to notify us immediately of
          any unauthorized use of your Account, and you agree not to share your Account with any
          other person or otherwise permit any other person to use your Account, even on a temporary
          basis. You{"\u2019"}re responsible for all activities that occur under your Account,
          whether or not you know about them. We may take actions we deem reasonably necessary to
          prevent fraud and abuse, including placing restrictions on user accounts or on the amount
          of content that can be accessed from the SWI Platform at any one time.
        </Text>

        <Text style={themed($text)}>
          5. We welcome feedback, comments and suggestions for improvements to the SWI Platform(
          {"\u201cFeedback\u201d"}). You can submit Feedback within the SWI Platform or by emailing
          us at feedback@sidekickplatform.com. You grant to us a non-exclusive, transferable,
          worldwide, perpetual, irrevocable, fully-paid, royalty-free license, with the right to
          sublicense, under any and all intellectual property rights that you own or control to use,
          copy, modify, create derivative works based upon and otherwise exploit the Feedback for
          any purpose, provided that we will not disclose the fact that the Feedback has been
          contributed by you.
        </Text>

        <Text style={themed($text)}>
          6. Content Ownership and Responsibility.{"\n\n"}
          (a) Definition. For purposes of these User Terms: {"\u201cContent\u201d"} means any
          information, data, content and other materials, including without limitation text,
          graphics, images, software, video, avatars, characters, and other works of authorship of
          any kind, in each case that are posted, generated, provided or otherwise made available
          through the SWI Platform, including, but not limited to User Content.
          {" \u201cUser Content\u201d"} means the data (including personal data and personally
          identifiable information), content, images, materials, and communications that you upload
          to or post on the SWI Platform in connection with the operation and use of the SWI
          Platform.{"\n\n"}
          (b) Organization Rights and Responsibilities. You acknowledge that as between us and the
          Organization, it is the Organization{"\u2019"}s sole responsibility to (i) inform you of
          any Organization policies or actions that may affect User Content and your ability to
          access User Content or any other aspects or areas of the SWI Platform; and (ii) respond to
          and resolve your questions and problems, and handle any disputes, in connection with User
          Content or your use of the SWI Platform.{"\n\n"}
          (c) Our Content Ownership. SWI does not claim any ownership rights in any User Content,
          and as between you and SWI, nothing in these User Terms will be deemed to restrict any
          rights that you may have to use and exploit User Content other than any rights and
          restrictions afforded to Organization under the Organization Agreement. Subject to the
          foregoing, SWI and its licensors hereby exclusively own and reserve all right, title and
          interest in and to the SWI Platform and Content, including all associated intellectual
          property rights. You acknowledge that the SWI Platform and Content are protected by
          copyright, trademark, and other laws of the United States and foreign countries. You agree
          not to remove, alter or obscure any copyright, trademark, service mark or other
          proprietary rights notices incorporated in or accompanying the SWI Platform or Content.
          {"\n\n"}
          (d) Rights in Data and Other User Content Granted by You.{"\n"}
          You retain any rights you have in User Content. By submitting, uploading, speaking,
          entering, or otherwise making User Content available through the SWI Platform, you grant
          SWI a limited, non-exclusive, worldwide license to host, process, transmit, reproduce,
          display, and use User Content solely as necessary to provide, operate, secure, maintain,
          support, and improve the SWI Platform for you and, where applicable, the organization that
          made the SWI Platform available to you.{"\n"}
          SWI will not sell your private User Content. SWI will not disclose your private User
          Content to other users except as directed by you, enabled by the functionality of the SWI
          Platform, authorized by the organization that made the SWI Platform available to you,
          described in our Privacy Notice, or required or permitted by law.{"\n"}
          For clarity, User Content may include personal information, chat communications, uploaded
          files, voice inputs, audio recordings, transcripts, and other content or information that
          you provide through the SWI Platform.{"\n\n"}
          (e) Aggregated and De-Identified Information.{"\n"}
          SWI may create and use aggregated or de-identified information derived from use of the SWI
          Platform to operate, secure, analyze, maintain, and improve the SWI Platform; develop and
          improve safety, reliability, and performance features; prevent abuse; generate analytics
          for organizations; and comply with legal obligations.{"\n"}
          SWI will not attempt to re-identify de-identified information except as permitted by law,
          such as to test whether de-identification methods are effective or to investigate
          security, fraud, abuse, or legal compliance.{"\n\n"}
          (f) No General AI Model Training Using Private User Content.{"\n"}
          SWI will not use your private conversations, uploaded files, audio recordings, voice
          inputs, transcripts, Inputs, Outputs, or other private User Content to train, fine-tune,
          benchmark, evaluate, or improve generalized AI models or third-party AI models, unless you
          or the organization that made the SWI Platform available to you expressly authorizes that
          use and applicable law permits it.{"\n"}
          SWI may use limited technical, usage, and diagnostic information to operate, secure,
          debug, maintain, and improve the safety, reliability, and functionality of the SWI
          Platform, as described in our Privacy Notice.{"\n\n"}
          (g) Your Responsibility for User Content. You are solely responsible for all User Content.
          You represent and warrant that neither User Content, nor your use and provision of User
          Content to be made available through the SWI Platform, nor any use of User Content by SWI
          on or through the SWI Platform will infringe, misappropriate or violate a third party
          {"\u2019"}s intellectual property rights, or rights of publicity or privacy, or result in
          the violation of any applicable law or regulation. You represent and warrant that all of
          your User Content and your activities in connection with the SWI Platform will, at all
          times, comply with (i) the User Terms; (ii) all applicable laws, rules, and regulations;
          and (iii) any other guidelines or requirements that we may make available to you from time
          to time.{"\n\n"}
          (h) Removal of User Content. The SWI Platform may allow you to remove User Content by
          specifically deleting it within the SWI Platform, subject to any applicable limitations
          imposed by Organization, if applicable. However, in certain instances, some of your User
          Content (such as any materials you upload or post, including on behalf of Organization, or
          anything that Organization wishes to retain, if applicable) may not be completely removed
          and copies of your User Content may continue to exist on the SWI Platform. We are not
          responsible or liable for the removal or deletion of (or failure to remove or delete) any
          of your User Content, including any data, information or other materials.{"\n\n"}
          (i) Disclosures Regarding Output. The content on the SWI Platform is provided for
          informational and EDUCATIONAL PURPOSES ONLY AND MAY BE GENERATED THROUGH SWI
          {"\u2019"}S OR ITS THIRD-PARTY LICENSORS{"\u2019"} ALGORITHMS OR ARTIFICIAL INTELLIGENCE
          TOOLS USED IN CONNECTION WITH OFFERING THE SWI PLATFORM. THE SWI PLATFORM MAY GENERATE AI
          AGENT OR CHATBOT RESPONSES (EACH, {"\u201cOUTPUT\u201d"}) IN RESPONSE TO: (I) YOUR
          INTERACTION OR CHAT; AND (II) USER CONTENT UPLOADED TO INFLUENCE THE BEHAVIOR OR OUTPUT OF
          THE AI AGENT OR AUTOMATED CHATBOT (COLLECTIVELY, {"\u201cINPUT\u201d"}). RELIANCE ON ANY
          CONTENT PROVIDED THROUGH THE SWI PLATFORM BY SWI OR OTHER THIRD-PARTY CONTENT PROVIDERS IS
          AT YOUR OWN RISK AND MAY BE SUBJECT TO ADDITIONAL TERMS, CONDITIONS, AND POLICIES OF THE
          THIRD-PARTY CONTENT PROVIDER.{"\n\n"}
          (j) Informational and Educational Use Only.{"\n"}
          The SWI Platform provides informational and educational support only. The SWI Platform
          does not provide medical advice, diagnosis, treatment, legal advice, financial advice,
          benefits determinations, insurance determinations, claims decisions, or emergency
          services.{"\n"}
          You should not rely on the SWI Platform as a substitute for professional judgment,
          official decisions, or advice from qualified professionals. If you have a medical
          emergency, mental health crisis, or believe you may be in danger, contact emergency
          services or an appropriate crisis-support resource immediately.{"\n"}
          If the SWI Platform provides information about government, healthcare, benefits,
          educational, employer, or organizational resources, that information is provided for
          navigation and educational purposes only and does not constitute an official determination
          by the relevant agency, provider, institution, employer, or organization.{"\n\n"}
          (k) AI/ML Disclaimer. Due to the nature of machine learning, Output may not be unique, and
          the SWI Platform may generate the same or similar output for SWI or a third party. GIVEN
          THE PROBABILISTIC NATURE OF MACHINE LEARNING, THE SWI PLATFORM MAY IN SOME SITUATIONS
          PRODUCE OUTPUT THAT IS INACCURATE, INCORRECT, OFFENSIVE OR OTHERWISE UNDESIRABLE. THE
          ACCURACY, QUALITY AND COMPLIANCE WITH APPLICABLE LAW OF THE OUTPUT IS DEPENDENT UPON AND
          COMMENSURATE WITH THAT OF THE INPUT PROVIDED AND YOUR COMPLIANCE WITH THESE TERMS, AND
          NOTWITHSTANDING ANYTHING ELSE SET OUT HEREIN, SWI WILL NOT HAVE ANY LIABILITY OR
          RESPONSIBILITY TO YOU OR ANY OTHER PERSON OR ENTITY FOR ANY LOSS OR DAMAGES RELATING TO OR
          ARISING FROM INPUT, OUTPUT OR THEIR USE.
        </Text>

        <Text style={themed($text)}>
          7. Rights and Terms for Apps.{"\n"}
          The SWI Platform may be offered via an App (defined above). The following terms apply if
          your use of and/or access to the SWI Platform is via an App:{"\n\n"}
          (a) Rights in App Granted by SWI. Subject to your compliance with these User Terms, SWI
          grants to you a limited, revocable, non-exclusive, non-transferable license, with no right
          to sublicense, to download and install a copy of the App on a mobile device, computer or
          other compatible hardware that you own or control and to run such copy of the App solely
          for your personal use of the SWI Platform and, if applicable, as authorized by the
          Organization. Except as expressly permitted in these User Terms, you may not: (i) copy,
          modify or create derivative works based on the App; (ii) distribute, transfer, sublicense,
          lease, lend or rent the App to any third party or otherwise use the App on a time sharing
          or service bureau basis; (iii) reverse engineer, decompile or disassemble the App; or (iv)
          make the functionality of the App available to multiple users through any means. SWI
          hereby reserves all rights in and to the App not expressly granted to you under these User
          Terms.{"\n\n"}
          (b) Accessing App from App Store. The following terms apply to any App accessed through or
          downloaded from any app store or distribution platform (like the Apple App Store or Google
          Play) where the App may now or in the future be made available (each an
          {" \u201cApp Provider\u201d"}). You acknowledge and agree that:{"\n"}
          {"\u2022"} These User Terms are concluded between you and SWI, and not with the App
          Provider, and SWI (not the App Provider), is solely responsible for the App.{"\n"}
          {"\u2022"} The App Provider has no obligation to furnish any maintenance and support
          services with respect to the App.{"\n"}
          {"\u2022"} In the event of any failure of the App to conform to any applicable warranty,
          the App Provider will have no warranty obligation whatsoever with respect to the App. Any
          other claims, losses, liabilities, damages, costs or expenses attributable to any failure
          to conform to any warranty will be the sole responsibility of SWI.{"\n"}
          {"\u2022"} The App Provider is not responsible for addressing any claims you have or any
          claims of any third party relating to the App or your possession and use of the App,
          including, but not limited to: (i) product liability claims; (ii) any claim that the App
          fails to conform to any applicable legal or regulatory requirement; and (iii) claims
          arising under consumer protection or similar legislation.{"\n"}
          {"\u2022"} In the event of any third-party claim that the App or your possession and use
          of that App infringes that third party{"\u2019"}s intellectual property rights, SWI will
          be solely responsible for the investigation, defense, settlement and discharge of any such
          intellectual property infringement claim to the extent required by these User Terms.{"\n"}
          {"\u2022"} The App Provider, and its subsidiaries, are third-party beneficiaries of these
          User Terms as related to your license to the App, and that, upon your acceptance of these
          User Terms, the App Provider will have the right (and will be deemed to have accepted the
          right) to enforce these User Terms as related to your license of the App against you as a
          third-party beneficiary thereof.{"\n"}
          {"\u2022"} You represent and warrant that (i) you are not located in a country that is
          subject to a U.S. Government embargo, or that has been designated by the U.S. Government
          as a terrorist-supporting country; and (ii) you are not listed on any U.S. Government list
          of prohibited or restricted parties.{"\n"}
          {"\u2022"} You must also comply with all applicable third-party terms of service when
          using the App.
        </Text>

        <Text style={themed($text)}>
          8. Device Storage and App Permissions{"\n\n"}
          (a) Device Storage. The App may store limited information on your device to support login,
          security, preferences, performance, and app functionality. Depending on your settings and
          use of the App, some information may remain on your device after you close the App or log
          out. You can remove locally stored app data by deleting the App, although deleting the App
          may not delete information stored on SWI{"\u2019"}s systems or the systems of the
          organization that made the SWI Platform available to you.{"\n\n"}
          (b) Voice Features and Microphone Access. If you use voice features, the App may request
          access to your device microphone. With your permission, the App may record or process your
          voice input to provide voice-enabled functionality, generate transcripts, respond to your
          requests, maintain security, troubleshoot issues, and support the SWI Platform. You can
          revoke microphone permission through your device settings. Our Privacy Notice describes
          whether audio recordings or transcripts are stored, how they are used, how long they are
          retained, and how you may request deletion where applicable.{"\n\n"}
          (c) Location Features. Some features may request access to location information, such as
          to help identify nearby resources or improve relevance of results. We will request device
          permissions where required. You can disable location permissions through your device
          settings. Our Privacy Notice describes the location information we collect, how we use it,
          and whether it is stored or shared.
        </Text>

        <Text style={themed($text)}>
          9. General Prohibitions and SWI{"\u2019"}s Enforcement Rights. You agree to not do any of
          the following in connection with your use of the SWI Platform:{"\n\n"}
          (a) Post, upload, publish, submit or transmit any Content that: (i) infringes,
          misappropriates or violates a third party{"\u2019"}s patent, copyright, trademark, trade
          secret, moral rights or other intellectual property rights, or rights of publicity or
          privacy; (ii) violates, or encourages any conduct that would violate, any applicable law
          or regulation or would give rise to civil liability; (iii) is fraudulent, false,
          misleading or deceptive; (iv) is defamatory, obscene, pornographic, vulgar or offensive;
          (v) promotes discrimination, bigotry, racism, hatred, harassment, abuse or harm against
          any individual or group; (vi) is violent, intimidating or threatening or promotes violence
          or actions that are threatening to any person or entity; or (vii) promotes illegal or
          harmful activities or substances;{"\n\n"}
          (b) Access, tamper with, or use non-public areas of the SWI Platform, SWI{"\u2019"}s
          computer systems, or the technical delivery systems of SWI{"\u2019"}s providers;{"\n\n"}
          (c) Attempt to probe, scan or test the vulnerability of the SWI Platform or any other SWI
          system or network or breach any security or authentication measures;{"\n\n"}
          (d) Avoid, bypass, remove, deactivate, impair, descramble or otherwise circumvent any
          technological measure implemented by SWI or any of SWI{"\u2019"}s providers, Organization
          or any other third party (including another user) to protect the SWI Platform or Content;
          {"\n\n"}
          (e) Attempt to access or search the SWI Platform or Content or download Content from the
          SWI Platform through the use of any engine, software, tool, agent, device or mechanism
          (including spiders, robots, crawlers, data mining tools or the like) other than the
          software and/or search agents provided by SWI to be expressly used for such purposes;
          {"\n\n"}
          (f) Send any unsolicited or unauthorized advertising, promotional materials, email, junk
          mail, spam, chain letters or other form of solicitation;{"\n\n"}
          (g) Attempt to decipher, decompile, disassemble or reverse engineer any of the software
          used to provide the SWI Platform or Content;{"\n\n"}
          (h) Interfere with, or attempt to interfere with, the access of any user, host or network,
          including, without limitation, sending a virus, overloading, flooding, spamming, or
          mail-bombing the SWI Platform;{"\n\n"}
          (i) Collect, use, transmit, disclose or store any personally identifiable information from
          the SWI Platform from other users of the SWI Platform;{"\n\n"}
          (j) Impersonate or misrepresent your affiliation with any person or entity;{"\n\n"}
          (k) Use any Confidential Information (as defined below), the SWI Platform or any of its
          component features or functionalities to develop, sell, license, commercialize or
          contribute to the development or commercialization of any product or service that could
          compete with the SWI Platform;{"\n\n"}
          (l) Provide any information that is intended to misinform, misdirect, mislead, or
          otherwise deceive any users of the SWI Platform or any other third party;{"\n\n"}
          (m) Violate any applicable law or regulation;{"\n\n"}
          (n) Use the SWI Platform or any Output in a manner that violates the usage policies of AI
          models employed by the SWI Platform;{"\n\n"}
          (o) Utilize the SWI Platform (including any AI models or derivatives thereof), SWI
          materials, Input or Output to train, improve or have trained or improved an AI model
          (e.g., engage in {"\u201cmodel scraping\u201d"});{"\n\n"}
          (p) Use the SWI Platform or any Output in a manner that violates any applicable usage
          policies; or{"\n\n"}
          (q) Encourage, assist, permit or enable any other individual to do any of the foregoing.
          {"\n\n"}
          Although we{"\u2019"}re not obligated to monitor access to or use of the SWI Platform or
          Content or to review or edit any Content, we have the right to do so for the purpose of
          operating the SWI Platform, to ensure compliance with these User Terms, the terms of any
          Organization Agreement and/or to comply with applicable law or other legal requirements.
          We reserve the right, but are not obligated, to remove or disable access to any Content,
          at any time and without notice, including, but not limited to, if we, at our sole
          discretion, consider any Content to be objectionable or in violation of these User Terms.
          We also reserve the right to terminate or suspend your access to the SWI Platform, at any
          time and without prior notice, if we, at our sole discretion, determine that you have
          violated any of the prohibitions included in these User Terms. We have the right to
          investigate violations of these User Terms or conduct that affects the SWI Platform. We
          may also consult and cooperate with law enforcement authorities to prosecute users who
          violate the law.{"\n"}
          If you believe that any Content or other items provided via the SWI Platform infringe,
          misappropriate, or violate your intellectual property rights, are abusive, harassing,
          offensive or hateful, or otherwise violate the prohibitions listed above, please email us
          at: feedback@sidekickplatform.com.
        </Text>

        <Text style={themed($text)}>
          10. Third Party Services. Certain features and functionalities within the SWI Platform as
          we determine in our sole discretion may also allow you and other Account holders to
          interface or interact with, access and/or use compatible third-party services, products,
          technology, websites and content (collectively, {"\u201cThird Party Services\u201d"})
          through the SWI Platform. You agree that (i) SWI does not provide any aspect of the Third
          Party Services and is not responsible for any compatibility issues, errors or bugs in the
          SWI Platform or Third Party Services caused in whole or in part by the Third Party
          Services or any update or upgrade thereto; and (ii) you or Organization are solely
          responsible for maintaining the Third Party Services and obtaining any associated licenses
          and consents necessary for you to utilize the Third Party Services in connection with the
          SWI Platform.
        </Text>

        <Text style={themed($text)}>
          11. Term and Termination.{"\n\n"}
          (a) These User Terms apply to you for the duration of your access to the SWI Platform as
          authorized by the Organization, until (i) terminated by the Organization or us, or (ii)
          until you cancel your Account or cease use of the SWI Platform.{"\n\n"}
          (b) Following SWI termination of its relationship with your Organization, we may terminate
          your access to and use of the SWI Platform, at our sole discretion, at any time and
          without notice to you.{"\n\n"}
          (c) You may cancel your Account at any time by sending an email to us at
          feedback@sidekickplatform.com or, if applicable, via the SWI Platform.{"\n\n"}
          (d) Following the termination of your relationship with your Organization, you may have
          the ability to continue to use your User Content and the Content (or a portion thereof)
          made available to you prior to such termination, in each case to the extent permitted by
          your Organization.{"\n\n"}
          (e) Upon any termination of these User Terms or cancellation of your Account, provisions
          that by their nature should survive termination will survive, including provisions
          relating to privacy and data practices, User Content, aggregated and de-identified
          information, AI and output disclaimers, informational and educational use, warranty
          disclaimers, indemnity, limitation of liability, governing law, dispute forum, and general
          terms.
        </Text>

        <Text style={themed($text)}>
          12. Warranty Disclaimers. THE SWI PLATFORM AND CONTENT ARE PROVIDED {"\u201cAS IS,\u201d"}
          WITHOUT WARRANTY OF ANY KIND. WITHOUT LIMITING THE FOREGOING, WE EXPLICITLY DISCLAIM ANY
          IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, QUIET ENJOYMENT
          AND NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING, COURSE OF
          PERFORMANCE, OR USAGE OF TRADE. NO INFORMATION, WHETHER ORAL OR WRITTEN, OR WHETHER
          OBTAINED FROM SWI, ORGANIZATION OR OTHERWISE, WILL CREATE ANY WARRANTY NOT EXPRESSLY
          STATED IN THESE USER TERMS. FURTHER, SWI MAKES NO WARRANTY THAT THE CONTENT, OR THE SWI
          PLATFORM OR ANY OF ITS COMPONENT FEATURES AND FUNCTIONALITIES WILL BE AVAILABLE ON AN
          UNINTERRUPTED, SECURE, OR ERROR-FREE BASIS. SWI DOES NOT WARRANT OR GUARANTEE: (I) ANY
          RESULTS FROM THE USE OF THE SWI PLATFORM OR CONTENT; OR (II) THAT THE SWI PLATFORM OR
          CONTENT WILL MEET YOUR EXPECTATIONS OR REQUIREMENTS. SWI HEREBY SPECIFICALLY DISCLAIMS ALL
          LIABILITY ARISING FROM ALL THIRD-PARTY SERVICES.
        </Text>

        <Text style={themed($text)}>
          13. Indemnity. You will indemnify and hold harmless SWI and its officers, directors,
          employees, shareholders and agents, from and against any claims, disputes, demands,
          liabilities, damages, losses, and costs and expenses, including, without limitation,
          reasonable legal and accounting fees arising out of or in any way connected with (i) your
          access to or use of the SWI Platform or Content, (ii) User Content, Input or Output, (iii)
          your violation of any third party{"\u2019"}s rights, including, but not limited to,
          intellectual property rights, right of privacy, right of publicity and confidentiality, or
          (iv) your violation of these User Terms.
        </Text>

        <Text style={themed($text)}>
          14. Limitation of Liability.{"\n\n"}
          (a) NEITHER SWI NOR ANY OTHER PARTY INVOLVED IN CREATING, PRODUCING, OR DELIVERING THE SWI
          PLATFORM OR CONTENT WILL BE LIABLE TO YOU FOR ANY INCIDENTAL, SPECIAL, PUNITIVE, EXEMPLARY
          OR CONSEQUENTIAL DAMAGES, OR DAMAGES FOR LOST PROFITS, LOST REVENUES, LOST SAVINGS, LOST
          BUSINESS OPPORTUNITY, LOSS OF DATA OR GOODWILL, SERVICE INTERRUPTION, COMPUTER DAMAGE OR
          SYSTEM FAILURE OR THE COST OF SUBSTITUTE SERVICES OF ANY KIND ARISING OUT OF OR IN
          CONNECTION WITH THESE USER TERMS OR FROM THE USE OF OR INABILITY TO USE THE SWI PLATFORM
          OR CONTENT, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), PRODUCT
          LIABILITY OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT SWI OR ANY OTHER PARTY HAS BEEN
          INFORMED OF THE POSSIBILITY OF SUCH DAMAGE, EVEN IF A LIMITED REMEDY SET FORTH HEREIN IS
          FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE. SOME JURISDICTIONS DO NOT ALLOW THE
          EXCLUSION OR LIMITATION OF LIABILITY FOR CONSEQUENTIAL OR INCIDENTAL DAMAGES, SO THE ABOVE
          LIMITATION MAY NOT APPLY TO YOU.{"\n\n"}
          (b) IN NO EVENT WILL SWI{"\u2019"}S TOTAL LIABILITY ARISING OUT OF OR IN CONNECTION WITH
          THESE USER TERMS OR FROM THE USE OF OR INABILITY TO USE OR ACCESS THE SWI PLATFORM OR
          CONTENT EXCEED ONE HUNDRED DOLLARS ($100).{"\n\n"}
          (c) THE EXCLUSIONS AND LIMITATIONS OF DAMAGES SET FORTH ABOVE ARE FUNDAMENTAL ELEMENTS OF
          THE BASIS OF THE BARGAIN BETWEEN SWI AND YOU.{"\n\n"}
          (d) Nothing in these User Terms limits or excludes liability to the extent such limitation
          or exclusion is prohibited by applicable law.
        </Text>

        <Text style={themed($text)}>
          15. Governing Law and Forum Choice. These User Terms and any action or proceeding related
          thereto will be governed by the laws of the State of Delaware without regard to its
          conflict of laws provisions. The exclusive jurisdiction for any dispute, claim or
          controversy arising out of or relating to these User Terms or the breach, termination,
          enforcement, interpretation or validity thereof or the use of the SWI Platform or Content
          will be the applicable state and federal courts located in or which jurisdiction covers
          Wilmington, Delaware, and you waive any objection to jurisdiction and venue in such
          courts.
        </Text>

        <Text style={themed($text)}>
          16. General Terms.{"\n\n"}
          (a) Entire Agreement. These User Terms constitute the entire and exclusive understanding
          and agreement between SWI and you regarding the SWI Platform and Content, and these User
          Terms supersede and replace any and all prior oral or written understandings or agreements
          between SWI and you regarding the SWI Platform and Content. If any provision of these User
          Terms is held invalid or unenforceable by a court of competent jurisdiction, that
          provision will be enforced to the maximum extent permissible and the other provisions of
          these User Terms will remain in full force and effect. You may not assign or transfer
          these User Terms, by operation of law or otherwise, without SWI{"\u2019"}s prior written
          consent. Any attempt by you to assign or transfer these User Terms, without such consent,
          will be null. SWI may freely assign or transfer these User Terms without restriction.
          Subject to the foregoing, these User Terms will bind and inure to the benefit of the
          parties, their successors and permitted assigns.{"\n\n"}
          (b) Notices. Any notices or other communications provided by SWI under these User Terms,
          including those regarding modifications to these User Terms, will be given: (i) via email;
          or (ii) by posting to the SWI Platform. For notices made by e-mail, the date of receipt
          will be deemed the date on which such notice is transmitted.{"\n\n"}
          (c) Waiver of Rights. SWI{"\u2019"}s failure to enforce any right or provision of these
          User Terms will not be considered a waiver of such right or provision. The waiver of any
          such right or provision will be effective only if in writing and signed by a duly
          authorized representative of SWI. Except as expressly set forth in these User Terms, the
          exercise by either party of any of its remedies under these User Terms will be without
          prejudice to its other remedies under these User Terms or otherwise.
        </Text>

        <Text style={themed($text)}>
          17. Contact Information. If you have any questions about these User Terms or the SWI
          Platform, please contact SWI at feedback@sidekickplatform.com.
        </Text>

        <View style={themed($buttonRow)}>
          <Button
            preset="sidekick"
            text="I Accept"
            style={themed($button)}
            onPress={onAcceptTerms}
          />
          <Button
            preset="sidekick"
            text={isDeclining ? "Logging out\u2026" : "I Don\u2019t Accept"}
            style={themed($button)}
            onPress={onDeclineTerms}
            disabled={isDeclining}
          />
        </View>
      </View>
    </Screen>
  )
}

const $screenContainer: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $contentContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  padding: spacing.lg,
})

const $content: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  gap: spacing.md,
})

const $title: ThemedStyle<TextStyle> = ({ spacing }) => ({
  marginBottom: spacing.xl,
  textAlign: "center",
})

const $text: ThemedStyle<TextStyle> = () => ({})

const $buttonRow: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexDirection: "row",
  gap: spacing.md,
  marginTop: spacing.xl,
})

const $button: ThemedStyle<ViewStyle> = () => ({ flex: 1 })
